/**
 * ApiComponent - Component for declarative API data loading and rendering
 * Provides HTML-based configuration for API calls with automatic rendering via TemplateManager
 *
 * Security: Uses window.http exclusively (no fallbacks) for consistent CSRF/interceptor protection
 *
 * @example
 * <div data-component="ApiComponent" data-endpoint="/api/users" data-method="GET">
 *   <template>
 *     <ul><li data-for="user of data">{user.name}</li></ul>
 *   </template>
 * </div>
 */
const ApiComponent = {
  config: {
    // HTTP configuration
    method: 'GET',        // HTTP method (GET, POST, PUT, DELETE)
    baseURL: '',          // Base URL for API endpoints
    autoload: true,       // Auto-load data when instance is created
    timeout: 30000,       // Request timeout (ms)
    cache: false,         // Enable response caching
    cacheTime: 60000,     // Cache duration (ms)
    retry: 3,             // Number of retry attempts on failure

    // UI text keys (will be translated via Now.translate)
    loadingText: 'Loading data...',
    errorText: 'An error occurred',
    emptyText: 'No data found',

    // Event handlers
    onSuccess: null,      // Callback on successful load
    onError: null,        // Callback on error
    onEmpty: null,        // Callback when no data returned

    // Development
    debug: false          // Enable debug logging
  },

  state: {
    instances: new Map(),  // Store all active instances
    initialized: false,
    cache: new Map()       // API response cache
  },

  /**
   * Create a new ApiComponent instance
   * @param {HTMLElement|string} element - DOM element or selector
   * @param {Object} options - Configuration options
   * @returns {Object|null} Instance object or null on error
   */
  create(element, options = {}) {
    // Verify HttpClient is available (fail-fast)
    if (!window.http || typeof window.http.get !== 'function') {
      const errorMsg = 'HttpClient (window.http) is required for ApiComponent';
      console.error('[ApiComponent]', errorMsg);
      const translatedMsg = window.Now?.translate ? Now.translate(errorMsg) : errorMsg;
      alert(translatedMsg);
      return null;
    }

    // Find element if selector string provided
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }

    if (!element) {
      console.error('[ApiComponent] Element not found');
      return null;
    }

    // Check if instance already exists
    const existingInstance = this.getInstance(element);
    if (existingInstance) {
      return existingInstance;
    }

    // Create new instance
    const instance = {
      id: 'api_' + Math.random().toString(36).substring(2, 11),
      element,
      options: {...this.config, ...this.extractOptionsFromElement(element), ...options},
      data: null,
      loading: false,
      error: null,
      timestamp: null,
      page: 1,
      totalPages: 1,
      pageSize: 10,
      totalItems: 0,
      polling: false,
      timer: null,
      abortController: null
    };

    // Initialize instance
    this.setup(instance);

    // Store instance
    this.state.instances.set(instance.id, instance);
    element.dataset.apiComponentId = instance.id;

    // Store reference on element for easy access from HTML
    element.apiInstance = instance;

    return instance;
  },

  /**
   * Setup and initialize an instance
   * @param {Object} instance - Instance object
   */
  setup(instance) {
    try {
      // Add api-component class without changing existing structure
      instance.element.classList.add('api-component');

      // Store template if present
      const templateEl = instance.element.querySelector('template');
      if (templateEl) {
        instance.templateContent = templateEl.innerHTML;
      } else if (instance.options.template) {
        // External template by ID
        const externalTemplate = document.getElementById(instance.options.template);
        if (externalTemplate && externalTemplate.tagName === 'TEMPLATE') {
          instance.templateContent = externalTemplate.innerHTML;
        }
      }

      // Bind events
      this.bindEvents(instance);

      // Auto-load data if enabled
      if (instance.options.autoload) {
        this.loadData(instance);
      }

      // Start polling if configured
      if (instance.options.polling && instance.options.pollingInterval) {
        this.startPolling(instance);
      }

      // Dispatch init event
      this.dispatchEvent(instance, 'init', {
        instance
      });

    } catch (error) {
      console.error('[ApiComponent] Setup error:', error);
      instance.error = error.message;
      this.renderError(instance);
    }
  },

  /**
   * Bind event handlers to instance
   * @param {Object} instance - Instance object
   */
  bindEvents(instance) {
    // Bind refresh events if configured
    if (instance.options.refreshEvent) {
      const events = instance.options.refreshEvent.split(',').map(e => e.trim());

      events.forEach(eventName => {
        if (!eventName) return;

        const handler = () => this.refresh(instance);

        // Store handlers for cleanup
        if (!instance.eventHandlers) {
          instance.eventHandlers = new Map();
        }

        instance.eventHandlers.set(eventName, handler);

        // Register event
        if (window.EventManager) {
          EventManager.on(eventName, handler);
        } else {
          document.addEventListener(eventName, handler);
        }
      });
    }

    // Add methods to instance for HTML access
    instance.refresh = () => {
      this.refresh(instance);
    };

    instance.loadMore = () => {
      this.loadMore(instance);
    };
  },

  /**
   * Load data from API
   * @param {Object} instance - Instance object
   * @param {boolean} append - Append to existing data (for pagination)
   */
  async loadData(instance, append = false) {
    try {
      // Cancel previous request if exists
      if (instance.abortController) {
        instance.abortController.abort();
      }

      // Create new AbortController
      instance.abortController = new AbortController();

      // Show loading state
      instance.loading = true;
      instance.error = null;
      this.renderLoading(instance);

      // Validate endpoint
      if (!instance.options.endpoint) {
        throw new Error('API endpoint is required');
      }

      // Check cache if enabled
      const cacheKey = this.createCacheKey(instance);
      if (instance.options.cache && !append) {
        const cached = this.state.cache.get(cacheKey);
        if (cached && (Date.now() < cached.expires)) {
          instance.data = cached.data;
          instance.loading = false;
          this.renderContent(instance);

          // Dispatch loaded event
          this.dispatchEvent(instance, 'loaded', {
            data: instance.data,
            fromCache: true
          });

          return;
        }
      }

      // Prepare URL
      let url = instance.options.endpoint;
      if (instance.options.baseURL && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = instance.options.baseURL + url;
      }

      // Prepare parameters
      const params = {...instance.options.params};

      // Add pagination params
      if (instance.options.pagination !== false) {
        if (instance.options.pageParam) {
          params[instance.options.pageParam] = instance.page;
        }
        if (instance.options.limitParam) {
          params[instance.options.limitParam] = instance.options.pageSize || instance.pageSize || 10;
        }
      }

      // Prepare options
      const reqOptions = {
        throwOnError: false,
        headers: instance.options.headers || {}
      };

      // Call API using HttpClient ONLY - no fallbacks
      if (!window.http || typeof window.http.request !== 'function') {
        throw new Error('HttpClient (window.http) is required but not available');
      }

      const method = instance.options.method.toUpperCase();
      let response;

      // Execute request based on method
      switch (method) {
        case 'POST':
          response = await window.http.post(url, instance.options.data || params, reqOptions);
          break;
        case 'PUT':
          response = await window.http.put(url, instance.options.data || params, reqOptions);
          break;
        case 'DELETE':
          response = await window.http.delete(url, reqOptions);
          break;
        default: // GET
          // Add params to URL for GET
          if (Object.keys(params).length > 0) {
            const queryParams = new URLSearchParams(params).toString();
            url = `${url}${url.includes('?') ? '&' : '?'}${queryParams}`;
          }
          response = await window.http.get(url, reqOptions);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update data from response
      this.updateDataFromResponse(instance, response, append);

      // Store in cache if enabled
      if (instance.options.cache && instance.data) {
        this.state.cache.set(cacheKey, {
          data: instance.data,
          expires: Date.now() + (instance.options.cacheTime || this.config.cacheTime)
        });
      }

      // Clear error
      instance.error = null;

      // Dispatch loaded event
      this.dispatchEvent(instance, 'loaded', {
        data: instance.data,
        response
      });

      // Render content
      this.renderContent(instance);

      // Call success callback if provided
      if (typeof instance.options.onSuccess === 'function') {
        instance.options.onSuccess.call(instance, instance.data, response);
      }

    } catch (error) {
      // Handle errors
      instance.error = error.message;
      console.error('[ApiComponent] Load error:', error);

      // Dispatch error event
      this.dispatchEvent(instance, 'error', {
        error: instance.error,
        message: error.message
      });

      // Render error
      this.renderError(instance);

      // Call error callback if provided
      if (typeof instance.options.onError === 'function') {
        instance.options.onError.call(instance, error);
      }

    } finally {
      // Update state
      instance.loading = false;
      instance.abortController = null;
    }
  },

  /**
   * Create cache key for request
   * @param {Object} instance - Instance object
   * @returns {string} Cache key
   */
  createCacheKey(instance) {
    const {endpoint, method, params, data} = instance.options;
    const paramString = params ? JSON.stringify(params) : '';
    const dataString = data ? JSON.stringify(data) : '';

    return `${method}:${endpoint}:${paramString}:${dataString}`;
  },

  /**
   * Update instance data from API response
   * @param {Object} instance - Instance object
   * @param {Object} response - HTTP response
   * @param {boolean} append - Append mode flag
   */
  updateDataFromResponse(instance, response, append = false) {
    // Extract data from response using configured path
    const dataPath = instance.options.dataPath || 'data';
    let responseData;

    // Get data by path
    if (dataPath && dataPath !== 'data') {
      responseData = this.getValueByPath(response, dataPath);
    } else {
      responseData = response.data;
    }

    // Handle empty data
    if (!responseData) {
      instance.data = [];

      // Dispatch empty event
      this.dispatchEvent(instance, 'empty');

      // Call empty callback if provided
      if (typeof instance.options.onEmpty === 'function') {
        instance.options.onEmpty.call(instance);
      }

      return;
    }

    // Append or replace data
    if (append && Array.isArray(responseData) && Array.isArray(instance.data)) {
      instance.data = [...instance.data, ...responseData];
    } else {
      instance.data = responseData;
    }

    // Update pagination info
    if (instance.options.pagination !== false) {
      const metaPath = instance.options.metaPath || 'meta';
      const meta = this.getValueByPath(response, metaPath) || {};

      instance.totalItems = meta.total || 0;
      instance.totalPages = meta.last_page || Math.ceil(instance.totalItems / instance.pageSize) || 1;
    }

    // Record timestamp
    instance.timestamp = Date.now();
  },

  /**
   * Get value from object by dot-notation path
   * @param {Object} obj - Source object
   * @param {string} path - Dot-notation path (e.g., 'data.users')
   * @returns {*} Value at path
   */
  getValueByPath(obj, path) {
    return path.split('.').reduce((o, p) => o?.[p], obj);
  },

  /**
   * Render loading state
   * @param {Object} instance - Instance object
   */
  renderLoading(instance) {
    const element = instance.element;

    // Clear other state classes
    element.classList.remove('error', 'empty', 'content');

    // Add loading class
    element.classList.add('loading');
  },

  /**
   * Render error state
   * @param {Object} instance - Instance object
   */
  renderError(instance) {
    const element = instance.element;

    // Clear other state classes
    element.classList.remove('loading', 'empty', 'content');

    // Add error class
    element.classList.add('error');

    // Log error
    console.error('[ApiComponent] Error:', instance.error);
  },

  /**
   * Render content with data
   * @param {Object} instance - Instance object
   */
  renderContent(instance) {
    const element = instance.element;

    // Clear state classes
    element.classList.remove('loading', 'error', 'empty');

    // Handle empty data
    if (!instance.data || (Array.isArray(instance.data) && instance.data.length === 0)) {
      element.classList.add('empty');
      return;
    }

    // Add content class
    element.classList.add('content');

    // Detect rendering mode
    const hasTemplate = instance.templateContent || element.querySelector('template');

    if (hasTemplate) {
      // Template Rendering Mode (existing)
      this.renderTemplateMode(instance);
    } else {
      // Simple Binding Mode (new - like DataSourceManager)
      this.renderSimpleBinding(instance);
    }

    // Dispatch content-rendered event
    this.dispatchEvent(instance, 'content-rendered', {
      data: instance.data
    });
  },

  /**
   * Render using template mode (existing behavior)
   * @param {Object} instance - Instance object
   */
  renderTemplateMode(instance) {
    const element = instance.element;

    // Create context for template processing
    const context = {
      ...instance,
      data: instance.data,
      state: {data: instance.data},
      ...this.options
    };

    // Process template via TemplateManager
    if (window.TemplateManager && typeof TemplateManager.processTemplate === 'function') {
      TemplateManager.processTemplate(element, context);
    }
  },

  /**
   * Render using simple binding mode (like DataSourceManager)
   * @param {Object} instance - Instance object
   */
  renderSimpleBinding(instance) {
    const element = instance.element;
    const data = instance.data;

    // Find all child elements with data-source-key
    const children = element.querySelectorAll('[data-source-key]');

    children.forEach(child => {
      const key = child.dataset.sourceKey;
      const attr = child.dataset.sourceAttr || 'textContent';
      const template = child.dataset.sourceTemplate;

      let value = data;
      if (key) {
        value = this.getValueByPath(data, key);
      }

      if (template) {
        value = this.applySimpleTemplate(template, data);
      }

      // Convert object to string if needed
      if (value && typeof value === 'object') {
        value = JSON.stringify(value);
      }

      // Update element based on attribute type
      if (attr === 'textContent') {
        child.textContent = value ?? '';
      } else if (attr === 'innerHTML') {
        child.innerHTML = value ?? '';
      } else if (attr === 'src' || attr === 'href') {
        child[attr] = value ?? '';
      } else {
        child.setAttribute(attr, value ?? '');
      }
    });
  },

  /**
   * Apply simple template with {key} placeholders
   * @param {string} template - Template string
   * @param {Object} data - Data object
   * @returns {string} Processed template
   */
  applySimpleTemplate(template, data) {
    return template.replace(/\{([^}]+)\}/g, (match, path) => {
      return this.getValueByPath(data, path.trim()) ?? '';
    });
  },

  /**
   * Refresh instance data (reset to page 1)
   * @param {Object|string|HTMLElement} instance - Instance, ID, or element
   */
  refresh(instance) {
    // Resolve instance
    if (typeof instance === 'string') {
      instance = this.state.instances.get(instance);
    } else if (instance instanceof HTMLElement) {
      instance = this.getInstance(instance);
    }

    if (!instance) return;

    // Reset pagination
    instance.page = 1;

    // Reload data
    this.loadData(instance);
  },

  /**
   * Load next page (pagination)
   * @param {Object|string|HTMLElement} instance - Instance, ID, or element
   */
  loadMore(instance) {
    // Resolve instance
    if (typeof instance === 'string') {
      instance = this.state.instances.get(instance);
    } else if (instance instanceof HTMLElement) {
      instance = this.getInstance(instance);
    }

    if (!instance) return;

    // Skip if already loading
    if (instance.loading) return;

    // Skip if no more pages
    if (instance.page >= instance.totalPages) return;

    // Increment page
    instance.page++;

    // Load data in append mode
    this.loadData(instance, true);
  },

  /**
   * Start polling for automatic data refresh
   * @param {Object|string|HTMLElement} instance - Instance, ID, or element
   */
  startPolling(instance) {
    // Resolve instance
    if (typeof instance === 'string') {
      instance = this.state.instances.get(instance);
    } else if (instance instanceof HTMLElement) {
      instance = this.getInstance(instance);
    }

    if (!instance) return;

    // Skip if already polling
    if (instance.polling) return;

    // Enable polling
    instance.polling = true;

    // Set interval
    const interval = parseInt(instance.options.pollingInterval) || 30000;
    instance.timer = setInterval(() => {
      // Load data if not currently loading
      if (!instance.loading) {
        this.loadData(instance);
      }
    }, interval);

    // Dispatch polling:start event
    this.dispatchEvent(instance, 'polling:start');
  },

  /**
   * Stop polling
   * @param {Object|string|HTMLElement} instance - Instance, ID, or element
   */
  stopPolling(instance) {
    // Resolve instance
    if (typeof instance === 'string') {
      instance = this.state.instances.get(instance);
    } else if (instance instanceof HTMLElement) {
      instance = this.getInstance(instance);
    }

    if (!instance) return;

    // Skip if not polling
    if (!instance.polling) return;

    // Clear timer
    if (instance.timer) {
      clearInterval(instance.timer);
      instance.timer = null;
    }

    // Disable polling
    instance.polling = false;

    // Dispatch polling:stop event
    this.dispatchEvent(instance, 'polling:stop');
  },

  /**
   * Submit data (POST request)
   * @param {Object|string|HTMLElement} instance - Instance, ID, or element
   * @param {Object} data - Data to submit
   */
  async submit(instance, data) {
    // Resolve instance
    if (typeof instance === 'string') {
      instance = this.state.instances.get(instance);
    } else if (instance instanceof HTMLElement) {
      instance = this.getInstance(instance);
    }

    if (!instance) return;

    // Set method to POST
    instance.options.method = 'POST';

    // Set data
    if (data) {
      instance.options.data = data;
    }

    // Load data
    return this.loadData(instance);
  },

  /**
   * Format date value
   * @param {string|Date} value - Date value
   * @param {string} format - Format string (Y-m-d H:i:s)
   * @returns {string} Formatted date
   */
  formatDate(value, format) {
    if (!value) return '';

    try {
      const date = new Date(value);

      // Validate date
      if (isNaN(date.getTime())) {
        return value;
      }

      // Replace format tokens
      return format
        .replace('Y', date.getFullYear())
        .replace('m', String(date.getMonth() + 1).padStart(2, '0'))
        .replace('d', String(date.getDate()).padStart(2, '0'))
        .replace('H', String(date.getHours()).padStart(2, '0'))
        .replace('i', String(date.getMinutes()).padStart(2, '0'))
        .replace('s', String(date.getSeconds()).padStart(2, '0'));
    } catch (e) {
      return value;
    }
  },

  /**
   * Format number value
   * @param {number} value - Number value
   * @param {number} decimals - Decimal places
   * @returns {string} Formatted number
   */
  formatNumber(value, decimals) {
    if (value === null || value === undefined) return '';

    const num = parseFloat(value);
    if (isNaN(num)) return value;

    return num.toLocaleString(undefined, {
      minimumFractionDigits: parseInt(decimals),
      maximumFractionDigits: parseInt(decimals)
    });
  },

  /**
   * Format currency value
   * @param {number} value - Number value
   * @param {string} currency - Currency symbol
   * @param {number} decimals - Decimal places
   * @returns {string} Formatted currency
   */
  formatCurrency(value, currency, decimals) {
    if (value === null || value === undefined) return '';

    const num = parseFloat(value);
    if (isNaN(num)) return value;

    return currency + ' ' + this.formatNumber(num, decimals);
  },

  /**
   * Clear all cached responses
   */
  clearCache() {
    this.state.cache.clear();
  },

  /**
   * Dispatch custom event
   * @param {Object} instance - Instance object
   * @param {string} eventName - Event name (without 'api:' prefix)
   * @param {Object} detail - Event detail data
   */
  dispatchEvent(instance, eventName, detail = {}) {
    if (!instance.element) return;

    const event = new CustomEvent(`api:${eventName}`, {
      bubbles: true,
      cancelable: true,
      detail: {
        instance,
        ...detail
      }
    });

    instance.element.dispatchEvent(event);

    // Emit via EventManager if available
    if (window.EventManager?.emit) {
      EventManager.emit(`api:${eventName}`, {
        instance,
        ...detail
      });
    }

    // Emit via Now if available
    if (window.Now?.emit) {
      Now.emit(`api:${eventName}`, {
        instance,
        ...detail
      });
    }
  },

  /**
   * Extract options from element data attributes
   * @param {HTMLElement} element - DOM element
   * @returns {Object} Options object
   */
  extractOptionsFromElement(element) {
    const options = {};
    const dataset = element.dataset;

    // Try data-props first (JSON format)
    if (dataset.props) {
      try {
        const props = JSON.parse(dataset.props);
        Object.assign(options, props);
      } catch (e) {
        console.warn('[ApiComponent] Invalid JSON in data-props:', e);
      }
    }

    // Read from individual data-* attributes
    if (!options.endpoint && dataset.endpoint) options.endpoint = dataset.endpoint;
    if (!options.method && dataset.method) options.method = dataset.method.toUpperCase();
    if (!options.baseURL && dataset.baseUrl) options.baseURL = dataset.baseUrl;
    if (options.autoload === undefined && dataset.autoload !== undefined)
      options.autoload = dataset.autoload !== 'false';
    if (options.cache === undefined && dataset.cache !== undefined)
      options.cache = dataset.cache === 'true';
    if (!options.cacheTime && dataset.cacheTime)
      options.cacheTime = parseInt(dataset.cacheTime);
    if (!options.timeout && dataset.timeout)
      options.timeout = parseInt(dataset.timeout);
    if (!options.retry && dataset.retry)
      options.retry = parseInt(dataset.retry);
    if (!options.template && dataset.template)
      options.template = dataset.template;
    if (!options.refreshEvent && dataset.refreshEvent)
      options.refreshEvent = dataset.refreshEvent;

    // Pagination options
    if (!options.pagination && dataset.pagination)
      options.pagination = dataset.pagination !== 'false';
    if (!options.pageParam && dataset.pageParam)
      options.pageParam = dataset.pageParam;
    if (!options.limitParam && dataset.limitParam)
      options.limitParam = dataset.limitParam;
    if (!options.pageSize && dataset.pageSize)
      options.pageSize = parseInt(dataset.pageSize);

    // Parse JSON strings
    if (!options.params && dataset.params) {
      try {
        options.params = JSON.parse(dataset.params);
      } catch (e) {
        console.warn('[ApiComponent] Invalid JSON in data-params');
      }
    }

    if (!options.data && dataset.data) {
      try {
        options.data = JSON.parse(dataset.data);
      } catch (e) {
        console.warn('[ApiComponent] Invalid JSON in data-data');
      }
    }

    if (!options.headers && dataset.headers) {
      try {
        options.headers = JSON.parse(dataset.headers);
      } catch (e) {
        console.warn('[ApiComponent] Invalid JSON in data-headers');
      }
    }

    // Path options
    if (!options.dataPath && dataset.dataPath) options.dataPath = dataset.dataPath;
    if (!options.metaPath && dataset.metaPath) options.metaPath = dataset.metaPath;

    return options;
  },

  /**
   * Get instance from element
   * @param {HTMLElement|string} element - Element or selector
   * @returns {Object|null} Instance or null
   */
  getInstance(element) {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }

    if (!element) return null;

    // Check for stored reference
    if (element.apiInstance) {
      return element.apiInstance;
    }

    // Check by ID
    const id = element.dataset.apiComponentId;
    if (id && this.state.instances.has(id)) {
      return this.state.instances.get(id);
    }

    // Search all instances
    for (const instance of this.state.instances.values()) {
      if (instance.element === element) {
        return instance;
      }
    }

    return null;
  },

  /**
   * Destroy instance and cleanup
   * @param {Object|string|HTMLElement} instance - Instance, ID, or element
   * @returns {boolean} Success flag
   */
  destroy(instance) {
    // Resolve instance
    if (typeof instance === 'string') {
      instance = this.state.instances.get(instance);
    } else if (instance instanceof HTMLElement) {
      instance = this.getInstance(instance);
    }

    if (!instance) return false;

    // Stop polling if active
    if (instance.polling) {
      clearInterval(instance.timer);
    }

    // Cancel active request
    if (instance.abortController) {
      instance.abortController.abort();
    }

    // Remove event handlers
    if (instance.eventHandlers) {
      instance.eventHandlers.forEach((handler, eventName) => {
        if (window.EventManager) {
          EventManager.off(eventName, handler);
        } else {
          document.removeEventListener(eventName, handler);
        }
      });
    }

    // Clear data
    instance.data = null;
    instance.loading = false;
    instance.error = null;

    // Clean DOM
    if (instance.element) {
      delete instance.element.apiInstance;
      delete instance.element.dataset.apiComponentId;

      // Remove api-component class
      instance.element.classList.remove('api-component');
    }

    // Dispatch destroy event
    this.dispatchEvent(instance, 'destroy');

    // Remove from Map
    if (instance.id) {
      this.state.instances.delete(instance.id);
    }

    return true;
  },

  /**
   * Initialize ApiComponent
   * @param {Object} options - Configuration options
   * @returns {Object} ApiComponent instance
   */
  async init(options = {}) {
    // Update config
    this.config = {...this.config, ...options};

    // Find and initialize elements with data-component="ApiComponent"
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initElements());
    } else {
      this.initElements();
    }

    this.state.initialized = true;
    return this;
  },

  /**
   * Initialize all elements with data-component="ApiComponent"
   */
  initElements() {
    document.querySelectorAll('[data-component="ApiComponent"]').forEach(element => {
      this.create(element);
    });
  }
};

/**
 * Register Component with ComponentManager
 */
if (window.ComponentManager) {
  const apiComponentDefinition = {
    template: null,

    validElement(element) {
      return element.classList.contains('api-component') ||
        element.dataset.component === 'api' ||
        element.dataset.component === 'ApiComponent' ||
        element.dataset.endpoint;
    },

    setupElement(element, state) {
      const options = ApiComponent.extractOptionsFromElement(element);
      const apiComponent = ApiComponent.create(element, options);

      element._apiComponent = apiComponent;
      return element;
    },

    beforeDestroy() {
      if (this.element && this.element._apiComponent) {
        ApiComponent.destroy(this.element._apiComponent);
        delete this.element._apiComponent;
      }
    }
  };

  ComponentManager.define('api', apiComponentDefinition);
}

/**
 * Register ApiComponent with Now.js framework
 */
if (window.Now?.registerManager) {
  Now.registerManager('api', ApiComponent);
}

/**
 * Make globally available
 */
window.ApiComponent = ApiComponent;

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ApiComponent.init());
} else {
  ApiComponent.init();
}
